/**
 * Posts the return-to-MSR2 feedback straight to a HubSpot form (APP-2283).
 *
 * HubSpot's form submission endpoint is built for browser calls — no token, no
 * backend — which is why this exists instead of a new API route for a feature
 * that is deleted at the October 2026 cutover. Submissions attach to the
 * member's existing contact by email.
 *
 * Deliberately the only raw `fetch` in the app: the shared axios instance shows
 * a global error modal for any failed non-GET request, and resets app state on
 * a 401 (`utils/axiosInstance.ts:61-82`). Routing feedback through it would put
 * an error modal in front of a member who is mid-exit, and let a third party's
 * 401 log them out.
 *
 * Portal matches the chat loader in RootLayout; ap1 is the account's data
 * region. The form GUID is public — it ships in HubSpot's own embed snippet —
 * so it lives here rather than in configuration. The feature flag is the kill
 * switch. Submissions carry `pageUri`, which is how a test-environment
 * submission is told apart from a real one in HubSpot.
 */
import * as Sentry from '@sentry/react';

const HUBSPOT_PORTAL_ID = '6938094';
const HUBSPOT_FORMS_API_BASE = 'https://api-ap1.hsforms.com';
const HUBSPOT_FORM_ID = '92fac447-6351-48d8-bb0f-4fa9b6c1b2b0';

const HUBSPOT_FIELD_NAMES = {
  email: 'email',
  feedback: 'msr3_return_feedback',
  reason: 'msr3_return_reason',
} as const;

export type Msr3ReturnFeedback = {
  email: string | undefined;
  feedback: string;
  reason: string;
};

const SUBMIT_URL = `${HUBSPOT_FORMS_API_BASE}/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`;

/**
 * Reported to Sentry rather than the member: a silent failure would hide a
 * misconfigured form for the whole life of the feature, with nothing to
 * distinguish "nobody had feedback" from "every submission was rejected".
 */
const reportFailure = (reason: string) =>
  Sentry.captureMessage('MSR3 return feedback not captured', {
    extra: { reason },
    level: 'warning',
  });

/** Past this the member waits too long just to leave the page. */
const SUBMIT_TIMEOUT_MS = 3000;

/**
 * Resolves to whether HubSpot accepted it, and never rejects: a member leaving
 * must not be blocked, or shown an error, because feedback capture is down.
 *
 * Callers must await this before navigating away. HubSpot only accepts
 * `application/json`, which forces a CORS preflight, and a preflight still in
 * flight when the page unloads means the POST is never dispatched at all —
 * `keepalive` cannot rescue a request that has not been sent yet.
 */
export const submitMsr3ReturnFeedback = async ({
  email,
  feedback,
  reason,
}: Msr3ReturnFeedback): Promise<boolean> => {
  const trimmedFeedback = feedback.trim();
  const trimmedReason = reason.trim();

  // Nothing to say is a legitimate skip: a blank or whitespace-only row would
  // only add noise to the CRM, and is not worth reporting.
  if (!trimmedReason && !trimmedFeedback) return false;

  // Having something to send but nobody to attach it to is a defect, and the
  // one case that would otherwise get the silence this file exists to avoid.
  if (!email) {
    reportFailure('no email address on the current user');

    return false;
  }

  const fields = [
    { name: HUBSPOT_FIELD_NAMES.email, value: email },
    ...(trimmedReason
      ? [{ name: HUBSPOT_FIELD_NAMES.reason, value: trimmedReason }]
      : []),
    ...(trimmedFeedback
      ? [{ name: HUBSPOT_FIELD_NAMES.feedback, value: trimmedFeedback }]
      : []),
  ];

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

  try {
    const response = await fetch(SUBMIT_URL, {
      body: JSON.stringify({
        context: {
          pageName: document.title,
          pageUri: `${window.location.origin}${window.location.pathname}`,
        },
        fields,
      }),
      headers: { 'Content-Type': 'application/json' },
      // Belt and braces: the caller awaits this, but keepalive also covers the
      // member closing the tab mid-request.
      keepalive: true,
      method: 'POST',
      signal: controller.signal,
    });

    if (!response.ok) reportFailure(`HTTP ${response.status}`);

    return response.ok;
  } catch (error) {
    reportFailure(error instanceof Error ? error.message : 'request failed');

    return false;
  } finally {
    clearTimeout(timeout);
  }
};
